import React, { Fragment } from "react";
import { appstr } from "@coreapp/utils/appstr";
import { Alert, Button, Divider, Flex, isNullish, Text, Icon } from "@theorgchartdev/storybook";
import cx from "classnames";
import { SelectableSearchMenu } from "@/appComponents/Blocks/Menus/BasicChartSearchMenu/SelectableSearchMenu";
import { appstrp } from "@coreapp/utils/appstrp";
import { getFeatureAccess } from "@/Utils/Misc";
import { enumFeaturesList } from "@coreapp/constants";
import { usePermissionCheck } from "@/hooks/usePermissionCheck";
import UpgradeCard from "@/appComponents/Blocks/Composites/UpgradePopover/UpgradeCard";
import { UpgradeInfoCard } from "@/appComponents/Blocks/Composites/UpgradePopover/UpgradeInfoCard";

export const AdditionalReportsTab = ({
  reportsData,
  validation,
  chartConfig,
  handleAddAuxSupervisor,
  handleSearchSelect,
  handleClearReport
}) => {
  const { showPricingAndPackaging } = usePermissionCheck();

  const getInputMessage = data => {
    if (data.isInUse) {
      return appstr("This supervisor is already selected");
    }
    return "";
  };

  const getInputStatus = data => {
    if (data.isInUse) {
      return "error";
    }
    if (!chartConfig?.headers?.includes(data.fieldName)) {
      return "warning";
    }
    return "default";
  };

  const canAddAuxSupervisor = reportsData?.length < 4;

  const arUpgradeMessageDynamicText = {
    template: appstr("ar upgrade template"),
    formatConfig: { bold1: { type: "bold" } },
    replaceDict: { bold1: appstr("accurate depiction") }
  };

  return (
    <Flex direction={"column"} gap={"16px"} overflow={"hidden"}>
      <Text
        dynamic={{
          formatConfig: { mappingPath: { type: "bold" } },
          replaceDict: { mappingPath: appstr("mappingPath") },
          template: appstr("Additional reports info")
        }}
        size={"small"}
      >
        {appstr("Additional reports info", { mappingPath: appstr("mappingPath") })}
      </Text>
      {getFeatureAccess(enumFeaturesList.auxMapping) && (
        <Flex>
          <Button
            dataTestId={"append-table-record-editor-add-auxiliary-supervisor"}
            modifier={"secondary"}
            size={"small"}
            onClick={handleAddAuxSupervisor}
            disabled={!canAddAuxSupervisor}
            tooltipProps={
              !canAddAuxSupervisor
                ? {
                    message: appstr("Additional reports limit"),
                    position: "right"
                  }
                : undefined
            }
            iconProps={{
              name: "plus-solid"
            }}
          >
            {appstr("Add assignment")}
          </Button>
        </Flex>
      )}
      {showPricingAndPackaging && !getFeatureAccess(enumFeaturesList.auxMapping) && (
        <>
          <Flex
            background={"var(--bg-brand-secondary)"}
            direction={"column"}
            gap={"16px"}
            alignItems={"center"}
            justifyContent={"center"}
            width={"100%"}
            padding={"24px"}
            borderRadius={"8px"}
          >
            <UpgradeCard
              iconName={"users-medical-regular"}
              featureKey={enumFeaturesList.auxMapping}
            >
              <UpgradeInfoCard.Body dynamic={arUpgradeMessageDynamicText}>
                {appstr("ar upgrade message")}
              </UpgradeInfoCard.Body>
            </UpgradeCard>
          </Flex>
        </>
      )}
      {validation.status === "error" && (
        <Flex width={"100%"}>
          <Alert
            severity={validation.status}
            isVisible={!validation.isValid}
            showIcon
            className={cx("oc-padding-small", "oc-text-small", "oc-data-source-details")}
          >
            {validation.message}
          </Alert>
        </Flex>
      )}
      {getFeatureAccess(enumFeaturesList.auxMapping) && (
        <Flex
          className={cx("oc-list-container", "ocn-list-scrollbar")}
          direction={"column"}
          gap={"16px"}
        >
          {reportsData.map((data, idx) => (
            <Fragment key={idx}>
              <Flex direction={"column"} gap={"8px"} width={"100%"}>
                <Text modifier={"bold"} size={"xsmall"}>
                  {appstrp("Additional reports to", ` ${idx > 0 ? idx : ""}`)}
                </Text>
                <SelectableSearchMenu
                  dataTestId={`append-table-record-editor-aux-supervisor-${idx}`}
                  inputSize="small"
                  displayRecents={false}
                  selectedItem={!isNullish(data.reportId) ? { label: data.reportName } : ""}
                  onRemoveItem={() => handleClearReport(idx)}
                  searchMenuProps={{
                    clearOnSelect: true,
                    clearable: true,
                    placeholder:
                      data?.reportId?.length > 0 ? "" : appstr("Search by name or title"),
                    searchProps: {
                      onResetInput: () => handleClearReport(idx)
                    },
                    inputProps: {
                      status: getInputStatus(data),
                      message: getInputMessage(data)
                    }
                  }}
                  projectFields={chartConfig?.headers}
                  handleSearchOptionClick={selectedItem => handleSearchSelect(idx, selectedItem)}
                  queryOptions={{ includeAux: false }}
                />
                <Divider vertical={false} />
              </Flex>
            </Fragment>
          ))}
        </Flex>
      )}
    </Flex>
  );
};
